import type {
  AssignTeamInput,
  AssignTeamResult,
  CreateStaffNoteInput,
  MembershipExpiryInput,
  MembershipExpiryResult,
  RemoveTeamResult,
  StaffCapabilities,
  StaffDirectoryResponse,
  StaffManageAuditResponse,
  StaffMemberSummary,
  StaffNote,
  StaffNoteCountRow,
  StaffNoteStatus,
  StaffNoteSubject,
  StaffNoteType,
  StaffNotesPage,
  StaffTeamsResponse,
  TesseractUserDetail,
  TesseractUserSearchRow,
  UpdateStaffNoteInput,
} from '../types/staffManager';

/**
 * Staff Manager — team management, member summaries and Staff Manager Notes.
 *
 * Auth: Cloudflare Access session cookies (the browser sends `Cf-Access-Jwt-Assertion`); the
 * backend forwards that same token to Tesseract on the user's behalf, so every Tesseract-backed
 * call is also subject to the caller's own Tesseract permissions. A 403 may therefore carry
 * `tesseract_permission_required` — surface it, it tells the user exactly what to ask for.
 *
 * Caching: capabilities, directory, teams and note counts are held for 60 s with in-flight
 * de-duplication (several panels read them at once); every mutation invalidates all of them.
 */

const STAFF_MANAGER_API_BASE_URL = 'https://spy.playcdu.co/api/notes/staff';
const CACHE_TTL_MS = 60 * 1000;

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

export class StaffManagerApiError extends Error {
  status: number;
  errorCode?: string;
  /** Tesseract permission the backend says the user is missing (403 only). */
  tesseractPermission?: string;

  constructor(status: number, message: string, errorCode?: string, tesseractPermission?: string) {
    super(message);
    this.name = 'StaffManagerApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.tesseractPermission = tesseractPermission;
  }

  /** True when the failure is about Tesseract configuration/permissions rather than ARGUS. */
  get isTesseractSide(): boolean {
    return !!this.tesseractPermission || (this.errorCode ?? '').startsWith('DELEGATION_') || this.status === 503;
  }
}

interface ErrorBody {
  error?: string;
  error_code?: string;
  tesseract_permission_required?: string;
  issues?: unknown;
}

class StaffManagerApiService {
  private cache = new Map<string, CacheEntry<unknown>>();
  private inflight = new Map<string, Promise<unknown>>();

  private async request<T>(endpoint: string, init: { method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'; body?: unknown } = {}): Promise<T> {
    const response = await fetch(`${STAFF_MANAGER_API_BASE_URL}${endpoint}`, {
      method: init.method ?? 'GET',
      headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as ErrorBody | null;
      throw new StaffManagerApiError(
        response.status,
        describeStatus(response.status, body),
        body?.error_code,
        body?.tesseract_permission_required,
      );
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }

  private cached<T>(key: string, force: boolean, loader: () => Promise<T>): Promise<T> {
    const now = Date.now();
    const hit = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!force && hit && now - hit.fetchedAt < CACHE_TTL_MS) return Promise.resolve(hit.data);
    const pending = this.inflight.get(key) as Promise<T> | undefined;
    if (pending) return pending;
    const promise = loader()
      .then((data) => {
        this.cache.set(key, { data, fetchedAt: Date.now() });
        return data;
      })
      .finally(() => {
        this.inflight.delete(key);
      });
    this.inflight.set(key, promise);
    return promise;
  }

  /** Synchronous read of the last successful fetch (for instant first paint). */
  peek<T>(key: 'capabilities' | 'directory' | 'teams' | 'noteCounts'): T | null {
    return (this.cache.get(key)?.data as T | undefined) ?? null;
  }

  invalidate(keys?: ('capabilities' | 'directory' | 'teams' | 'noteCounts')[]): void {
    if (!keys) {
      this.cache.clear();
      return;
    }
    for (const k of keys) this.cache.delete(k);
  }

  // --- Capabilities / directory / teams -------------------------------------------------------

  getCapabilities(options: { force?: boolean } = {}): Promise<StaffCapabilities> {
    return this.cached('capabilities', !!options.force, () => this.request<StaffCapabilities>('/manage/capabilities'));
  }

  getDirectory(options: { force?: boolean } = {}): Promise<StaffDirectoryResponse> {
    return this.cached('directory', !!options.force, () => this.request<StaffDirectoryResponse>('/manage/directory'));
  }

  getTeams(options: { force?: boolean } = {}): Promise<StaffTeamsResponse> {
    return this.cached('teams', !!options.force, () => this.request<StaffTeamsResponse>('/manage/teams'));
  }

  async searchUsers(query: string): Promise<TesseractUserSearchRow[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    const data = await this.request<{ users: TesseractUserSearchRow[] }>(`/manage/users/search?q=${encodeURIComponent(q.slice(0, 64))}`);
    return data.users ?? [];
  }

  getUser(userId: string): Promise<TesseractUserDetail> {
    return this.request<TesseractUserDetail>(`/manage/users/${encodeURIComponent(userId)}`);
  }

  // --- Membership mutations ------------------------------------------------------------------

  async assignToTeam(input: AssignTeamInput): Promise<AssignTeamResult> {
    const result = await this.request<AssignTeamResult>('/manage/teams/assign', { method: 'POST', body: input });
    this.invalidate(['directory', 'teams']);
    return result;
  }

  async removeFromTeam(input: { user_id: string; team_id: string }): Promise<RemoveTeamResult> {
    const result = await this.request<RemoveTeamResult>('/manage/teams/remove', { method: 'POST', body: input });
    this.invalidate(['directory', 'teams']);
    return result;
  }

  async setMembershipExpiry(input: MembershipExpiryInput): Promise<MembershipExpiryResult> {
    const result = await this.request<MembershipExpiryResult>('/manage/teams/expiry', { method: 'POST', body: input });
    this.invalidate(['directory', 'teams']);
    return result;
  }

  getManageAudit(options: { limit?: number; action?: string } = {}): Promise<StaffManageAuditResponse> {
    const params = new URLSearchParams();
    if (options.limit) params.set('limit', String(options.limit));
    if (options.action) params.set('action', options.action);
    const qs = params.toString();
    return this.request<StaffManageAuditResponse>(`/manage/audit${qs ? `?${qs}` : ''}`);
  }

  // --- Unified member summary ---------------------------------------------------------------

  getMember(discordId: string, options: { uuid?: string | null } = {}): Promise<StaffMemberSummary> {
    const qs = options.uuid ? `?uuid=${encodeURIComponent(options.uuid)}` : '';
    return this.request<StaffMemberSummary>(`/members/${encodeURIComponent(discordId)}${qs}`);
  }

  // --- Staff Manager Notes ------------------------------------------------------------------

  async getNotesForSubject(subject: StaffNoteSubject): Promise<StaffNote[]> {
    const params = new URLSearchParams();
    if (subject.discord_id) params.set('discord_id', subject.discord_id);
    if (subject.uuid) params.set('uuid', subject.uuid);
    if (subject.user_id) params.set('user_id', subject.user_id);
    if ([...params.keys()].length === 0) return [];
    return this.request<StaffNote[]>(`/notes?${params.toString()}`);
  }

  getAllNotes(options: { page?: number; limit?: number; type?: StaffNoteType; status?: StaffNoteStatus; q?: string } = {}): Promise<StaffNotesPage> {
    const params = new URLSearchParams();
    params.set('page', String(options.page ?? 1));
    params.set('limit', String(options.limit ?? 50));
    if (options.type) params.set('type', options.type);
    if (options.status) params.set('status', options.status);
    if (options.q && options.q.trim()) params.set('q', options.q.trim().slice(0, 100));
    return this.request<StaffNotesPage>(`/notes/all?${params.toString()}`);
  }

  getNoteCounts(options: { force?: boolean } = {}): Promise<StaffNoteCountRow[]> {
    return this.cached('noteCounts', !!options.force, async () => {
      const data = await this.request<{ counts: StaffNoteCountRow[] }>('/notes/counts');
      return data.counts ?? [];
    });
  }

  async createNote(input: CreateStaffNoteInput): Promise<StaffNote> {
    const note = await this.request<StaffNote>('/notes', { method: 'POST', body: input });
    this.invalidate(['noteCounts']);
    return note;
  }

  async updateNote(id: string, input: UpdateStaffNoteInput): Promise<StaffNote> {
    const note = await this.request<StaffNote>(`/notes/${encodeURIComponent(id)}`, { method: 'PATCH', body: input });
    this.invalidate(['noteCounts']);
    return note;
  }

  async deleteNote(id: string): Promise<void> {
    await this.request<{ ok: boolean }>(`/notes/${encodeURIComponent(id)}`, { method: 'DELETE' });
    this.invalidate(['noteCounts']);
  }
}

function describeStatus(status: number, body: ErrorBody | null): string {
  const serverMessage = body?.error;
  switch (status) {
    case 400:
      return serverMessage || 'The request was rejected as invalid.';
    case 403:
      if (body?.tesseract_permission_required) {
        return serverMessage || `Your Tesseract account lacks the ${body.tesseract_permission_required} permission.`;
      }
      return serverMessage || 'You do not have permission to do that.';
    case 404:
      return serverMessage || 'Not found.';
    case 409:
      return serverMessage || 'That member is already on the team.';
    case 429:
      return serverMessage || 'Too many requests. Try again shortly.';
    case 502:
      return serverMessage || 'Tesseract could not be reached.';
    case 503:
      return serverMessage || 'Tesseract delegation is not configured on the server yet.';
    default:
      return serverMessage || `API Error: ${status}`;
  }
}

export const staffManagerApiService = new StaffManagerApiService();
